import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Button,
  ButtonGroup,
  closeDialog,
  openConfirmDialog,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, Center, HStack, Link, Show, Text, useElementSize, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetFinancingFieldsQuery} from '@dms/api/core';
import {FinancingCompositionScene, FinancialProductType} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {BuiltInFontStyles} from './components/BuiltInFontStyles';
import {EditorCanvas} from './components/EditorCanvas';
import {EditorToolbar} from './components/EditorToolbar';
import {GenerateBadgeDialog} from './components/GenerateBadgeDialog';
import {LayerInspector} from './components/LayerInspector';
import {LayerList} from './components/LayerList';
import {useCompositionEditorState} from './hooks/useCompositionEditorState';
import {useCompositionFonts} from './hooks/useCompositionFonts';
import {
  CompositionServerPreviewTarget,
  useCompositionServerPreview,
} from './hooks/useCompositionServerPreview';
import {useLayerImageUrls} from './hooks/useLayerImageUrls';
import {CompositionEditorMode} from './types/CompositionEditorMode';
import {createBadgeLayers} from './utils/createBadgeLayers';
import {
  createFinancingTemplateScene,
  FinancingTemplateLook,
} from './utils/createFinancingTemplateScene';
import {createGeneratedImageLayer} from './utils/createGeneratedImageLayer';
import {createImageLayer} from './utils/createImageLayer';
import {createPhotoLayer} from './utils/createPhotoLayer';
import {createRectLayer} from './utils/createRectLayer';
import {createTextLayer} from './utils/createTextLayer';
import {getCanvasViewScale} from './utils/getCanvasViewScale';
import {getOverflowLayerIds} from './utils/getOverflowLayerIds';
import {getPreviewWarnings} from './utils/getPreviewWarnings';

export type CompositionEditorApplyResult = {
  scene: FinancingCompositionScene;
  previewUrl: string | null;
};

interface CompositionEditorProps extends RequiredTestIdProps {
  initialScene: FinancingCompositionScene | null;
  mode?: CompositionEditorMode;
  // Financing mode only: selects the data-field catalog offered for text layers and the sample
  // values of the server preview.
  financialProductType?: FinancialProductType;
  // Financing mode only: which vehicle photo the server preview places into the photo slot.
  sourcePhotoIndex?: number | null;
  onApply: (result: CompositionEditorApplyResult) => void;
  onCancel: () => void;
}

// Wide enough for the inspector's two-column rows and segment controls at the smallest supported
// desktop width (1280px) without any control being pushed past the panel edge.
const SIDE_PANEL_WIDTH = 80;
const CANVAS_AREA_MIN_HEIGHT = 120;
// The layer list takes the panel height the toolbar leaves free, but never less than this share
// of the panel: on a short viewport the toolbar scrolls inside its own area instead.
const LAYER_LIST_MIN_HEIGHT = '40%';
// Vertical space the dialog chrome takes: the xLarge dialog margin (2×4), its header and the body
// padding (2×4). The editor claims the rest so the side panels scroll vertically inside their own
// area (never sideways — a wide control would otherwise shift the whole panel out of view) and
// the Cancel/Apply footer always stays on screen below them.
const EDITOR_HEIGHT = 'calc(100vh - 32px - 65px - 32px)';
// A badge is a rect plus a text layer.
const BADGE_LAYER_COUNT = 2;
const GENERATE_BADGE_DIALOG_ID = 'composition-editor-generate-badge';
const TEXT_OVERFLOW_DIALOG_ID = 'composition-editor-text-overflow';
const NO_PREVIEW_PROBLEMS = {warnings: [], issues: []};

export function CompositionEditor(props: CompositionEditorProps) {
  const mode = props.mode ?? 'watermark';
  const isFinancingMode = mode === 'financing';
  const editor = useCompositionEditorState(props.initialScene, mode);
  const {fonts, isLoading: isLoadingFonts} = useCompositionFonts();
  const imageUrls = useLayerImageUrls(props.initialScene);
  const previewTarget: CompositionServerPreviewTarget =
    isFinancingMode && isNotNil(props.financialProductType)
      ? {
          mode: 'financing',
          financialProductType: props.financialProductType,
          sourcePhotoIndex: props.sourcePhotoIndex ?? null,
        }
      : {mode: 'watermark'};
  const serverPreview = useCompositionServerPreview(previewTarget);
  // The canvas paints `{{KEY}}` tokens with the samples of the product's data fields.
  const {data: financingFields = []} = useGetFinancingFieldsQuery(
    previewTarget.mode === 'financing'
      ? {financialProductType: previewTarget.financialProductType}
      : skipToken
  );
  const [canvasAreaRef, canvasAreaWidth, canvasAreaHeight] = useElementSize<HTMLDivElement>();

  const scale = getCanvasViewScale(editor.scene.canvas, canvasAreaWidth, canvasAreaHeight);
  // Warnings and overflow outlines come from the last server render of this exact scene only.
  const currentPreview = serverPreview.getPreviewFor(editor.scene);
  const isShowingPreview = serverPreview.isShowingPreviewOf(editor.scene);
  const {layerWarnings, sceneWarnings} = getPreviewWarnings(
    currentPreview ?? NO_PREVIEW_PROBLEMS,
    editor.scene.layers
  );
  const overflowLayerIds = getOverflowLayerIds(currentPreview?.issues ?? []);

  const handleAddImage = (fileId: string, url: string) => {
    imageUrls.registerImageUrl(fileId, url);
    editor.actions.addLayers([createImageLayer(editor.scene.canvas, fileId)]);
  };

  const openGenerateBadgeDialog = () =>
    openDialog(
      <GenerateBadgeDialog
        onGenerated={(badge, size) => {
          // The response `url` is a storage resize URL without dimensions, which storage rejects.
          imageUrls.resolveImageUrl(badge.fileId);
          editor.actions.addLayers([
            createGeneratedImageLayer(editor.scene.canvas, badge.fileId, size),
          ]);
          closeDialog(GENERATE_BADGE_DIALOG_ID);
        }}
        onCancel={() => closeDialog(GENERATE_BADGE_DIALOG_ID)}
        data-testid={suffixTestId('generateBadge', props)}
      />,
      {
        id: GENERATE_BADGE_DIALOG_ID,
        title: i18n.t('entity.watermark.composition.ai.labels.title'),
        'data-testid': suffixTestId('generateBadgeDialog', props),
      }
    );

  // Text that overflows its layer is clipped in every generated photo, so applying a scene whose
  // render clips text needs an explicit confirmation. A stale render is refreshed first.
  const handleApply = () => {
    const scene = editor.scene;
    void serverPreview.getCurrentPreview(scene).then((preview) => {
      const apply = () => props.onApply({scene, previewUrl: preview?.base64Url ?? null});
      const overflowLayerCount = getOverflowLayerIds(preview?.issues ?? []).length;
      if (overflowLayerCount === 0) {
        apply();
        return;
      }
      openDialog(
        <Text>
          {i18n.t('entity.watermark.composition.labels.textOverflowConfirmation', {
            count: overflowLayerCount,
          })}
        </Text>,
        {
          id: TEXT_OVERFLOW_DIALOG_ID,
          size: 'small',
          title: i18n.t('entity.watermark.composition.labels.previewWarnings'),
          buttons: [
            {
              variant: 'secondary',
              title: i18n.t('general.actions.cancel'),
              onClick: () => closeDialog(TEXT_OVERFLOW_DIALOG_ID),
              'data-testid': suffixTestId('textOverflow-cancel', props),
            },
            {
              variant: 'primary',
              title: i18n.t('entity.promoPhotos.actions.saveAnyway'),
              onClick: () => {
                closeDialog(TEXT_OVERFLOW_DIALOG_ID);
                apply();
              },
              'data-testid': suffixTestId('textOverflow-confirm', props),
            },
          ],
          'data-testid': suffixTestId('textOverflow', props),
        }
      );
    });
  };

  // A template replaces the whole scene, so a scene with layers is replaced only after a confirm.
  // Undo brings the previous scene back.
  const startFromTemplate = (
    financialProductType: FinancialProductType,
    look: FinancingTemplateLook
  ) => {
    const insertTemplate = () =>
      editor.actions.replaceScene(createFinancingTemplateScene(look, financialProductType));
    if (editor.scene.layers.length === 0) {
      insertTemplate();
      return;
    }
    openConfirmDialog({
      text: i18n.t('entity.promoPhotos.composition.templates.replaceConfirmation'),
      onConfirm: insertTemplate,
      'data-testid': suffixTestId('templateConfirm', props),
    });
  };

  const selectWarningLayer = (layerId: string) => {
    serverPreview.hidePreview();
    editor.actions.selectLayer(layerId);
  };

  return (
    <>
      <BuiltInFontStyles />
      <VStack spacing={3} height={EDITOR_HEIGHT} data-testid={props['data-testid']}>
        <HStack spacing={4} align="stretch" grow={1} minHeight={0}>
          <VStack width={SIDE_PANEL_WIDTH} shrink={0} spacing={4} minHeight={0}>
            <Box flexShrink={1} minHeight={0} overflowY="auto" overflowX="hidden">
              <EditorToolbar
                canvas={editor.scene.canvas}
                hasSelectedLayer={isNotNil(editor.selectedLayer)}
                canAddLayer={editor.canAddLayers(1)}
                canAddBadge={editor.canAddLayers(BADGE_LAYER_COUNT)}
                canUndo={editor.canUndo}
                canRedo={editor.canRedo}
                onAddText={() =>
                  editor.actions.addLayers([
                    createTextLayer(
                      editor.scene.canvas,
                      i18n.t('entity.watermark.composition.labels.defaultText')
                    ),
                  ])
                }
                onAddImage={handleAddImage}
                onGenerateBadge={openGenerateBadgeDialog}
                onAddRect={() => editor.actions.addLayers([createRectLayer(editor.scene.canvas)])}
                onAddBadge={() =>
                  editor.actions.addLayers(
                    createBadgeLayers(
                      editor.scene.canvas,
                      i18n.t('entity.watermark.composition.labels.defaultText')
                    )
                  )
                }
                canAddPhotoSlot={!editor.hasPhotoLayer}
                onAddPhotoSlot={
                  isFinancingMode
                    ? () => editor.actions.addLayers([createPhotoLayer(editor.scene.canvas)])
                    : undefined
                }
                onStartFromTemplate={
                  previewTarget.mode === 'financing'
                    ? (look) => startFromTemplate(previewTarget.financialProductType, look)
                    : undefined
                }
                onAlign={(alignment) =>
                  editor.selectedLayer &&
                  editor.actions.alignLayer(editor.selectedLayer.id, alignment)
                }
                onUndo={editor.actions.undo}
                onRedo={editor.actions.redo}
                onCanvasSizeChange={editor.actions.setCanvasSize}
                onBackgroundChange={editor.actions.setBackground}
                data-testid={suffixTestId('toolbar', props)}
              />
            </Box>
            <Separator spacing={0} />
            <VStack grow={1} basis={0} minHeight={LAYER_LIST_MIN_HEIGHT}>
              <LayerList
                layers={editor.scene.layers}
                selectedLayerId={editor.selectedLayer?.id ?? null}
                onSelect={editor.actions.selectLayer}
                onReorder={editor.actions.reorderLayer}
                canDuplicate={editor.canAddLayers(1)}
                onDuplicate={editor.actions.duplicateLayer}
                onRemove={editor.actions.removeLayer}
                data-testid={suffixTestId('layers', props)}
              />
            </VStack>
          </VStack>

          <Box flex={1} minWidth={0}>
            <VStack spacing={2} height="100%">
              <Show when={layerWarnings.length > 0 || sceneWarnings.length > 0}>
                <VStack spacing={1}>
                  <Alert
                    variant="warning"
                    title={i18n.t('entity.watermark.composition.labels.previewWarnings')}
                    message={sceneWarnings.length > 0 ? sceneWarnings : undefined}
                    data-testid={suffixTestId('previewWarnings', props)}
                  />
                  {/* A layer warning selects its layer, so it can be fixed right away. */}
                  {layerWarnings.map((warning) => (
                    <Link
                      key={`${warning.layerId}-${warning.message}`}
                      size="small"
                      leftIcon="alert/warning"
                      title={warning.message}
                      onClick={() => selectWarningLayer(warning.layerId)}
                      data-testid={suffixTestId(`previewWarning-${warning.layerId}`, props)}
                    />
                  ))}
                </VStack>
              </Show>
              <Show when={isShowingPreview}>
                <Alert
                  variant="info"
                  message={i18n.t('entity.watermark.composition.labels.showingServerPreview')}
                  data-testid={suffixTestId('previewInfo', props)}
                />
              </Show>
              {/* The financing preview never gets a vehicle, so its values are always samples. */}
              <Show when={previewTarget.mode === 'financing' && isShowingPreview}>
                <Text
                  size="small"
                  color="secondary"
                  data-testid={suffixTestId('sampleData', props)}
                >
                  {i18n.t('entity.promoPhotos.composition.labels.sampleData')}
                </Text>
              </Show>
              <Box
                ref={canvasAreaRef}
                flex={1}
                minHeight={CANVAS_AREA_MIN_HEIGHT}
                backgroundColor="palettes.neutral.20.100"
                borderRadius="small"
                padding={4}
                overflow="hidden"
              >
                <Center height="100%">
                  <EditorCanvas
                    scene={editor.scene}
                    scale={scale}
                    selectedLayerId={editor.selectedLayer?.id ?? null}
                    fonts={fonts}
                    financingFields={financingFields}
                    previewUrl={isShowingPreview ? (currentPreview?.base64Url ?? null) : null}
                    overflowLayerIds={overflowLayerIds}
                    getImageUrl={imageUrls.getImageUrl}
                    onSelectLayer={editor.actions.selectLayer}
                    onCommitLayerBox={editor.actions.resizeLayer}
                    data-testid={suffixTestId('canvas', props)}
                  />
                </Center>
              </Box>
            </VStack>
          </Box>

          <Box width={SIDE_PANEL_WIDTH} flexShrink={0} overflowY="auto" overflowX="hidden">
            <LayerInspector
              layer={editor.selectedLayer}
              canvas={editor.scene.canvas}
              fonts={fonts}
              isLoadingFonts={isLoadingFonts}
              financialProductType={isFinancingMode ? props.financialProductType : undefined}
              onChange={editor.actions.updateLayer}
              onFontDeleted={editor.actions.clearDeletedFont}
              onResize={editor.actions.resizeLayer}
              onImageUploaded={imageUrls.registerImageUrl}
              data-testid={suffixTestId('inspector', props)}
            />
          </Box>
        </HStack>

        <Separator spacing={0} />

        <HStack justify="space-between" align="center" shrink={0}>
          <Button
            variant="secondary"
            leftIcon="action/visibility"
            title={
              isShowingPreview
                ? i18n.t('entity.watermark.composition.actions.backToEditing')
                : i18n.t('general.actions.preview')
            }
            isLoading={serverPreview.isLoading}
            onClick={() =>
              isShowingPreview
                ? serverPreview.hidePreview()
                : void serverPreview.showPreview(editor.scene)
            }
            data-testid={suffixTestId('preview', props)}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={props.onCancel}
              data-testid={suffixTestId('cancel', props)}
            />
            <Button
              variant="primary"
              title={i18n.t('entity.watermark.composition.actions.applyGraphic')}
              isDisabled={editor.scene.layers.length === 0}
              isLoading={serverPreview.isLoading}
              onClick={handleApply}
              data-testid={suffixTestId('apply', props)}
            />
          </ButtonGroup>
        </HStack>
      </VStack>
    </>
  );
}
